import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {environment} from '../../../environments/environment';
import {lastValueFrom, Subject, takeUntil} from "rxjs";
import {MessageService} from "primeng/api";
import {WindowRefService} from "../../helpers/windowref.service";
import {DialogService} from 'primeng/dynamicdialog';
import {ApiCallService} from "../../helpers/api-call.service";
import {NavigationService} from '../../helpers/navigation.service'
import {DataServiceService} from 'src/app/helpers/data.service';
import {VerifyCodeComponent} from "../../shared/verify-code/verify-code.component";
import {CreateAccountInMb} from "../../shared/dialogs/create-account-in-mb/create-account-in-mb";

@Component({
    selector: 'app-tiktok-coupon-step1',
    templateUrl: './tiktok-coupon-step1.component.html',
    styleUrls: ['./tiktok-coupon-step1.component.scss'],
    standalone: false
})
export class TiktokCouponStep1Component implements OnInit, OnDestroy {
  componentDestroyed: Subject<boolean> = new Subject();
  currentYear: number = new Date().getFullYear();
  showDialog: boolean = false;
  dialogHeader: string = '';
  dialogBody: string = 'An invitation was sent to your mailbox.';
  redeemButton: boolean = true;
  loading: boolean = false;
  geo: string = 'Global';
  score: number | undefined;
  receiveMsgFlag = false;
  openTabIndex: any = undefined;
  metricData: any = {
    link: {
      amount: '2,850',
      text: 'Accounts Linked',
    },
    redeemed: {
      amount: '1,583',
      text: 'Coupons Redeemed',
    },
    saved: {
      amount: '$633,200',
      text: 'Saved',
    }
  };
  page = 'tiktok';
  public handler: any;

  constructor(private router: Router, public dialogService: DialogService,
              private messageService: MessageService,
              private navigationService: NavigationService,
              private winRef: WindowRefService, private apiCallService: ApiCallService, public data: DataServiceService) {
    this.data.setHeaderMenuSelection('14');
  }

  ngOnInit(): void {
    this.init();
    this.handler = this.receiveMessage.bind(this);
    document.body.scrollTop = 0;
    this.startPolling()
  }

  ngOnDestroy(): void {
    this.componentDestroyed.next(true);
    this.componentDestroyed.complete();
  }

  goToEligibleLink(channel: string) {
    this.data.goToEligibleLink(channel);
  }

  changeSelectedTabIndex(e: any) {
    if (e) {
      this.openTabIndex = e.index;
    }
  }

  get platformURL(): string {
    return `${environment.apiUrl}/v1/tiktok_platform/login`
  }

  async init() {
    const init$ = this.apiCallService.init(this.page);
    const response = await lastValueFrom(init$);
    if (response.token) {
      sessionStorage.setItem(this.page + 'Token', response.token);
    }
  }

  async openRecaptcha(op: string): Promise<void> {
    const ipValidate$ = this.apiCallService.ipValidate(this.page);
    const response = await lastValueFrom(ipValidate$);
    if (response.enabled) {
      const token = await this.apiCallService.executeRecaptcha(op);
      if (token) {
        const params = {
          token: token,
          sessionToken: sessionStorage.getItem(this.page + 'Token'),
          action: op
        };
        await this.getRecaptcha(params, op);
      }
    } else {
      this.messageService.add({
        key: 'msgTemplate',
        severity: 'warn',
        summary: '',
        detail: "You've hit today's limit for account linking. Feel free to explore other media channel coupons, or try again tomorrow. Thanks!.",
        data: "You've hit today's limit for account linking. Feel free to explore other media channel coupons, or try again tomorrow. Thanks!",
        closable: true,
        sticky: true
      });
    }
  }

  async getRecaptcha(params: any, op: string) {
    const recaptchaAssessment = this.apiCallService.recaptchaAssessment(params);
    const recaptchaResponse = await lastValueFrom(recaptchaAssessment);
    this.score = recaptchaResponse.score;
    if (this.score && this.score >= 0.8) {
      if (op === 'link') {
        this.linkAccount();
      }
      if (op === "create") {
        this.dialogService.open(CreateAccountInMb, {
          styleClass: 'coupon-selection-dialog',
          modal: true,
          draggable: false,
          resizable: false,
          data: this.page
        });
      }
    } else {
      this.messageService.add({
        key: 'msgTemplate',
        severity: 'warn',
        summary: '',
        detail: "High risk indicated by Google reCAPTCHA, please contact us.",
        data: "High risk indicated by Google reCAPTCHA, please contact us.",
        closable: true,
        sticky: true
      });
    }
  }

  linkAccount(): void {
    this.loading = true;
    this.winRef.nativeWindow.removeEventListener('message', this.handler, false);
    this.winRef.reportGtagEventNew('clickOnLinkAccount');
    const params = this.winRef.queryString;
    const suffixUrl = `geo=${this.geo}&score=${this.score}&page=${this.page}`;
    const loginUrl = params.length ? this.platformURL + '/' + params + `&${suffixUrl}` : this.platformURL + `/?${suffixUrl}`;
    const wndParams = `scrollbars=yes,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=600,height=800,top=50,left=200`;
    const newWindow = this.winRef.nativeWindow.open(loginUrl, 'Authenticate', wndParams);

    const checkWindowClosedInterval = setInterval(() => {
      if (newWindow && newWindow.closed) {
        clearInterval(checkWindowClosedInterval);
        if (!this.receiveMsgFlag) {
          this.loading = false;
          this.winRef.nativeWindow.removeEventListener('message', this.handler);
        }
        this.receiveMsgFlag = false;
      }
    }, 1000);

    this.winRef.nativeWindow.addEventListener('message', this.handler);
  }

  receiveMessage(event: any): any {
    const detail = "TikTok is having trouble connecting your account. Please try again later or contact couponer@adcore.com";
    const severity = 'error';
    const summary = 'We apologize for the inconvenience.';
    this.messageService.clear();
    if (event.origin === `${environment.apiUrl}` && event.data && event.data.refresh_token) {
      this.receiveMsgFlag = true;
      this.winRef.nativeWindow.removeEventListener('message', this.handler, false);
      if (event.data.taskId) {
        if (event.data.requiresVerification === false) {
          this.startTikTokSafeguardTask(event.data);
        } else {
          this.showVerifyCode(event.data);
        }
      } else {
        this.loading = false;
        this.messageService.add({
          key: 'msgTemplate',
          severity: severity,
          summary: summary,
          detail: detail,
          closable: true,
          sticky: true
        });
      }
    }
  }

  private showVerifyCode(verifyInfo: any): void {
    const ref = this.dialogService.open(VerifyCodeComponent, {
      header: 'Verify Email',
      data: verifyInfo,
      styleClass: 'verify-code-dialog create-acc-form-dialog',
      modal: true
    });
    ref.onClose.pipe(takeUntil(this.componentDestroyed)).subscribe((returnData: any) => {
      if (returnData) {
        this.startTikTokSafeguardTask(returnData);
      } else {
        this.loading = false;
      }
    });
  }

  private startTikTokSafeguardTask(returnData: any): void {
    this.apiCallService.ttLinkTaskId = returnData.taskId;
    sessionStorage.setItem('tiktokInfo', JSON.stringify(returnData));
    const params = {
      ...returnData,
      sessionToken: sessionStorage.getItem(this.page + 'Token'),
      geo: this.geo,
      page: this.page
    };
    this.apiCallService.startSafeguardTask(params, 'tiktok');
    this.startPolling();
  }

  private formatTikTokErrorDetail(errInfo: string | undefined): string {
    if (!errInfo) {
      return "TikTok is having trouble connecting your account. Please try again later or contact couponer@adcore.com";
    }
    if (errInfo.includes('Session token is invalid or expired.')) {
      return `${errInfo} Please refresh the page and try again.`;
    }
    if (errInfo.includes('reCAPTCHA validation failed.')) {
      return `${errInfo} Please try again later or try a different browser.`;
    }
    return `${errInfo} Please try a different account.`;
  }

  startPolling() {
    let detail = "TikTok is having trouble connecting your account. Please try again later or contact couponer@adcore.com";
    let severity = 'error';
    let summary = 'We apologize for the inconvenience.';
    if (this.apiCallService.ttLinkTaskId && this.apiCallService.ttLinkTaskId.length) {
      this.loading = true;
      const taskId = this.apiCallService.ttLinkTaskId;
      let timeout = true;
      this.apiCallService.statusPoll(this.page).pipe()
        .subscribe({
          next: (res) => {
            timeout = false;
            const status = res.status;
            if (status === 'completed') {
              sessionStorage.setItem('tiktokPassedList', JSON.stringify(res.passedList));
              this.router.navigate(['/tt/step2']);
            } else {
              switch (status) {
                case 'accepted':
                  detail = "The account is already linked to Couponer's TikTok partner account.";
                  severity = 'success';
                  summary = 'Great news!';
                  break
                case 'error':
                  detail = this.formatTikTokErrorDetail(res.errInfo);
                  severity = 'error';
                  break
                case 'spend':
                  detail = "This TikTok account is not eligible for linking right now. Please try a different account.";
                  severity = 'warn';
                  break
                case 'oversize':
                  detail = "The selected TikTok Business Center is too large for this flow. Please try a smaller account.";
                  severity = 'warn';
                  break
                case 'timeout':
                  detail = "Task timeout. Please try again later.";
                  severity = 'error';
                  break
                default:
                  detail = this.formatTikTokErrorDetail(res.errInfo);
                  console.log(`Connect Error: ${res.errInfo}`);
              }
              this.messageService.add({
                key: 'msgTemplate',
                severity: severity,
                summary: summary,
                detail: detail,
                closable: true,
                sticky: true
              })
            }
          },
          error: (reason) => {
            console.error({reason});
          },
          complete: () => {
            this.loading = false;
            this.apiCallService.deleteTtLinkTaskId();
            if (timeout) {
              this.messageService.add({
                key: 'msgTemplate',
                severity: 'error',
                summary: '',
                detail: "Task timeout. Please try again later.",
                closable: true,
                sticky: true
              });
              this.apiCallService.timeoutTask(taskId, sessionStorage.getItem('tiktokToken'));
            }
          }
        });
    }
  }

  got_it(action: string): void {
    this.navigationService.canNavigateToStep3TikTok = true;
    this.router.navigate(['/tt/step3'], {queryParams: {action: action}});
  }

  close(event: any): void {
    if (event.message.severity === 'success') {
      this.got_it('link');
    }
  }
}
